import { injectConfigManifest } from '../interface/config';
import type { IContainer } from '../interface/container';

/* static-imports, no code-splitting */
import cache from '../impl/cache';
import cacheController from '../impl/cachecontroller';
import env from '../impl/env';
import log from '../impl/log';
import query from '../impl/querystring';
import promiser from '../impl/promise';
import mimeType from '../impl/mimeType';
import { bindProgressPromise, injectProgressPromise } from '../interface/promise';
import { bindENV, injectENV } from '../interface/env';
import { bindFetch, IFetcher, IFetchRequest } from '../interface/fetch';
import { bindLog, injectLog } from '../interface/log';
import { bindQueryURL } from '../interface/query';
import { bindZip, IZip } from '../interface/zip';
import { bindKVStorage, IKVStorage } from '../interface/kvStorage';
import { bindBrowser, IBrowser } from '../interface/browser';
import { bindCache, bindCacheController } from '../interface/cache';
import { bindTranslationStorage, ITranslationStorage } from '../interface/translationStorage';
import { bindMimeType } from '../interface/mimeType';

function _bindENV(container: IContainer) {
    bindENV(env(container), container);
}

function _bindFetch(container: IContainer) {
    let val: IFetcher | null = null;

    const resolver = () => {
        return new Promise<IFetcher>(async function (resolve, reject) {
            let rejection = null;

            const progressPromiser = injectProgressPromise(container);

            try {
                if (!val) {
                    const fetcher = (await import('../impl/fetch')).default;
                    val = (requestPayload: IFetchRequest, timeout?: number) => {
                        return fetcher(progressPromiser, requestPayload, timeout);
                    };
                }
            } catch (error) {
                rejection = error;
            }

            if (val) {
                resolve(val);
            } else {
                console.error('Fetch failed');
                reject(rejection || 'Import failed for fetcher');
            }
        });
    };

    bindFetch(resolver, container);
}

function _bindLog(container: IContainer) {
    bindLog(log(container), container);
}

function _bindQueryString(container: IContainer) {
    bindQueryURL(query, container);
}

function _bindZip(container: IContainer) {
    let val: IZip | null = null;

    const resolver = () => {
        return new Promise<IZip>(async function (resolve, reject) {
            let rejection = null;

            try {
                if (!val) {
                    val = (await import('../impl/zip')).default;
                }
            } catch (error) {
                rejection = error;
            }

            if (val) {
                resolve(val);
            } else {
                reject(rejection || 'Import failed for zip');
            }
        });
    };

    bindZip(resolver, container);
}

function _bindKVStorage(container: IContainer) {
    let val: IKVStorage | null = null;

    const resolver = () => {
        return new Promise<IKVStorage>(async function (resolve, reject) {
            let rejection = null;

            const dbName = `${injectConfigManifest(container).short_name ?? 'site'}-util-storage`;
            const storeName = 'keyvaluepairs';
            const version = 1;
            const logger = injectLog(container);

            try {
                if (!val) {
                    val = (await import('../impl/kvstorage')).default(
                        dbName,
                        storeName,
                        version,
                        logger,
                    );
                }
            } catch (error) {
                rejection = error;
            }

            if (val) {
                resolve(val);
            } else {
                reject(rejection || 'Import failed for kvstorage');
            }
        });
    };

    bindKVStorage(resolver, container);
}

function _bindBrowser(container: IContainer) {
    let val: IBrowser | null = null;

    const resolver = () => {
        return new Promise<IBrowser>(async function (resolve, reject) {
            let rejection = null;

            try {
                if (!val) {
                    val = (await import('../impl/browser')).default();
                }
            } catch (error) {
                rejection = error;
            }

            if (val) {
                resolve(val);
            } else {
                reject(rejection || 'Import failed for browser');
            }
        });
    };

    bindBrowser(resolver, container);
}

function _bindCache(container: IContainer) {
    bindCache(cache(), container);
}

function _bindCacheController(container: IContainer) {
    bindCacheController(cacheController(), container);
}

function _bindMimeType(container: IContainer) {
    bindMimeType(mimeType());
}

function _bindPromise(container: IContainer) {
    bindProgressPromise(promiser, container);
}

function _bindTranslationStorage(container: IContainer) {
    let val: ITranslationStorage | null = null;

    const resolver = () => {
        return new Promise<ITranslationStorage>(async function (resolve, reject) {
            let rejection = null;

            try {
                if (!val) {
                    const envLoad = injectENV(container);
                    val = (await import('../impl/translationstorage')).default(envLoad);
                }
            } catch (error) {
                rejection = error;
            }

            if (val) {
                resolve(val);
            } else {
                reject(rejection || 'Import failed for translation-storage');
            }
        });
    };

    bindTranslationStorage(resolver, container);
}

export function bind(container: IContainer): Promise<void> {
    return new Promise(async function (resolve, reject) {
        try {
            const binders = [
                _bindENV,
                _bindPromise,
                _bindFetch,
                _bindLog,
                _bindQueryString,
                _bindZip,
                _bindKVStorage,
                _bindBrowser,
                _bindCache,
                _bindCacheController,
                _bindTranslationStorage,
                _bindMimeType,
            ];

            for (const binder of binders) {
                binder.call(null, container);
            }

            resolve();
        } catch (error) {
            reject(error || 'Unknown error binding utils');
        }
    });
}
